import {
  CommentAuthorType,
  InlineComment,
  InlineEditItem,
  InlineEditResolution,
  UserCommentState,
} from '@amzn/awscat-pattern-library-service-model';
import _ from 'lodash';

import { log } from '../loggers/Logger';
import { ContentEditorService } from './ContentEditorService';
import { InlineCommentManager } from './InlineCommentManager';
import { InlineEditsManager } from './InlineEditsManager';
import { NodeIdMappings } from './InlineTextOperations';
import { Unsubscribe } from './TypedEmitter';

/**
 * How long to wait after the last change before reconciling, so an accept or reject
 * reaches the content editor, inline edits, and inline comments before we act.
 */
const SETTLE_DELAY_MS = 500;

function toNodeIdSet(mappings: NodeIdMappings): ReadonlySet<string> {
  const nodeIdSets = [
    mappings.root,
    ...Object.values(mappings.subContents),
    ...Object.values(mappings.contentSections),
    ...Object.values(mappings.epics).flatMap((epic) => [epic.title, epic.description]),
    ...Object.values(mappings.troubleshootings).flatMap((item) => [item.issue, item.solution]),
  ];
  return new Set(nodeIdSets.flatMap((nodeIds) => [...nodeIds]));
}

function findOrphanedEdits(
  edits: readonly InlineEditItem[],
  nodeIds: ReadonlySet<string>,
): readonly InlineEditItem[] {
  return edits.filter((edit) => !edit.resolved && !nodeIds.has(edit.nodeId));
}

function findOrphanedScanComments(
  comments: readonly InlineComment[],
  nodeIds: ReadonlySet<string>,
): readonly InlineComment[] {
  return comments.filter(
    (comment) =>
      // Only scanner comments anchor to a node; human comments anchor to a whole section.
      comment.authorType === CommentAuthorType.SCANNER &&
      comment.state === UserCommentState.PENDING &&
      comment.nodeId !== undefined &&
      comment.nodeId !== '' &&
      !nodeIds.has(comment.nodeId),
  );
}

/** Resolves findings that no longer anchor to valid content, checking whenever the content, edits, or comments change. */
export class FindingReconciler {
  private readonly _contentEditor: ContentEditorService;
  private readonly _inlineEdits: InlineEditsManager;
  private readonly _inlineComments: InlineCommentManager;
  private readonly _unsubscribes: readonly Unsubscribe[];
  private readonly _debouncedReconcile = _.debounce(() => this.reconcile(), SETTLE_DELAY_MS);

  // Inputs of the last full pass, used for reference comparisons to skip passes when nothing changed.
  // `_lastContentId` also tells whether the open content's first pass has run.
  private _lastContentId: string | undefined = undefined;
  private _lastEdits: readonly InlineEditItem[] | undefined = undefined;
  private _lastComments: readonly InlineComment[] | undefined = undefined;
  private _lastMappings: NodeIdMappings | undefined = undefined;

  constructor(
    contentEditor: ContentEditorService,
    inlineEdits: InlineEditsManager,
    inlineComments: InlineCommentManager,
  ) {
    this._contentEditor = contentEditor;
    this._inlineEdits = inlineEdits;
    this._inlineComments = inlineComments;

    // The three services load independently, so any of them may be the last
    // one to finish; listen to all and let `reconcile` decide when to act.
    const onServiceChanged = (): void => this.onServiceChanged();
    this._unsubscribes = [
      contentEditor.on('changed', onServiceChanged),
      inlineEdits.on('changed', onServiceChanged),
      inlineComments.on('changed', onServiceChanged),
    ];

    // Catch up in case all three finished loading before we subscribed.
    this.onServiceChanged();
  }

  /** Stops listening to the services and drops any pending pass. */
  dispose(): void {
    for (const unsubscribe of this._unsubscribes) unsubscribe();
    this._debouncedReconcile.cancel();
  }

  private onServiceChanged(): void {
    // Run right away until the open content's first pass is done: nothing the
    // user did can be in flight yet, and the finding counts are correct sooner.
    if (this._lastContentId !== this._contentEditor.loadedDocumentHandle?.contentId) {
      this.reconcile();
      return;
    }

    // Later changes wait until edits settle, so an accept or reject the user just
    // made reaches the managers before we decide the finding is orphaned.
    this._debouncedReconcile();
  }

  private reconcile(): void {
    // Only editable content can be changed by the user, and only authors
    // should change finding state.
    if (!this._contentEditor.isEditable) return;

    // Wait until all three services have finished loading.
    const contentId = this._contentEditor.loadedDocumentHandle?.contentId;
    const mappings = this._contentEditor.nodeIdMappings;
    const openedEdits = this._inlineEdits.openedEdits;
    const openedComments = this._inlineComments.openedComments;
    if (!contentId || !mappings || !openedEdits || !openedComments) return;

    // A manager may still hold the previous content while the next one loads.
    if (openedEdits.contentId !== contentId || openedComments.contentId !== contentId) return;

    // Skip when none of the inputs changed since the last pass; the services
    // replace these objects on every real change, so a reference check is enough.
    const { edits } = openedEdits;
    const { comments } = openedComments;
    if (
      contentId === this._lastContentId &&
      edits === this._lastEdits &&
      comments === this._lastComments &&
      mappings === this._lastMappings
    ) {
      return;
    }
    this._lastContentId = contentId;
    this._lastEdits = edits;
    this._lastComments = comments;
    this._lastMappings = mappings;

    const nodeIds = toNodeIdSet(mappings);
    const orphanedEdits = findOrphanedEdits(edits, nodeIds);
    const orphanedComments = findOrphanedScanComments(comments, nodeIds);
    if (orphanedEdits.length === 0 && orphanedComments.length === 0) return;

    // Record which findings get resolved automatically, so a missing finding can be traced later.
    log.warn(
      {
        contentId,
        editIds: orphanedEdits.map((edit) => edit.editId),
        commentIds: orphanedComments.map((comment) => comment.commentId),
      },
      '[FindingReconciler] resolving findings whose node is gone',
    );

    // A failed resolve reverts the finding, so a later pass retries it.
    for (const edit of orphanedEdits) {
      this.rejectEdit(edit.editId);
    }
    for (const comment of orphanedComments) {
      this.ignoreComment(comment.commentId);
    }
  }

  private rejectEdit(editId: string): void {
    this._inlineEdits.tryResolveEdit(editId, InlineEditResolution.REJECTED).catch((err) => {
      log.error({ err, editId }, '[FindingReconciler] failed to reject orphaned edit');
    });
  }

  private ignoreComment(commentId: string): void {
    this._inlineComments.resolveThreadAs(commentId, UserCommentState.IGNORED).catch((err) => {
      log.error(
        { err, commentId },
        '[FindingReconciler] failed to ignore orphaned scanner comment',
      );
    });
  }
}
